import Modal from "@/components/atoms/Modal";
import { Select } from "@/components/atoms/Select";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import SoftphoneLine, { SoftphoneLineForm } from "@/interfaces/SoftphoneLine";
import {
  useStoreSoftphoneLineMutation,
  useUpdateSoftphoneLineMutation,
} from "@/services/softphoneLines";
import { useGetUsersQuery } from "@/services/users";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { toast } from "react-toastify";

interface UserOption {
  value: string;
  label: string;
}

const StoreSoftphoneLineModal: ForwardRefRenderFunction<{
  showOrHiddenModal: (line?: SoftphoneLine) => void;
}> = (_, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const [editingLine, setEditingLine] = useState<SoftphoneLine | undefined>();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [storeSoftphoneLineAsync] = useStoreSoftphoneLineMutation();
  const [updateSoftphoneLineAsync] = useUpdateSoftphoneLineMutation();
  const { data: usersData } = useGetUsersQuery({ pageSize: 1000 });

  const userOptions: UserOption[] = useMemo(
    () =>
      (usersData?.data ?? [])
        .filter((user) => user.isActive)
        .map((user) => ({
          value: user.id,
          label: `${user.fullname} — ${user.organisation?.name ?? "Sans organisme"} (${user.email})`,
        })),
    [usersData],
  );

  const {
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors },
  } = useForm<SoftphoneLineForm & { userOptions: UserOption[] }>();

  const showOrHiddenModal = (line?: SoftphoneLine) => {
    setEditingId(line?.id ?? null);
    setEditingLine(line);
    setSubmitError(null);
    reset({
      kavkomExtension: line?.kavkomExtension ?? "",
      kavkomUserUuid: line?.kavkomUserUuid ?? "",
      label: line?.label ?? "",
      status: line?.status ?? "active",
      userOptions: (line?.users ?? (line?.user ? [line.user] : [])).map(
        (user) => ({
          value: user.id,
          label: `${user.fullname} — ${user.organisation?.name ?? "Sans organisme"} (${user.email})`,
        }),
      ),
    });
    ModalRef.current?.showOrHiddenModal();
  };

  useImperativeHandle(ref, () => ({ showOrHiddenModal }));

  const supportsMembers =
    !editingLine || editingLine.protocolVersion !== undefined;
  const maxAccounts = supportsMembers ? 2 : 1;
  const onSubmit: SubmitHandler<
    SoftphoneLineForm & { userOptions: UserOption[] }
  > = async ({ userOptions, ...rest }) => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    const data: SoftphoneLineForm = {
      ...rest,
      ...(supportsMembers
        ? { userIds: userOptions.map((option) => option.value) }
        : { userId: userOptions[0]?.value ?? null }),
    };
    try {
      const promise = editingId
        ? updateSoftphoneLineAsync({ id: editingId, data }).unwrap()
        : storeSoftphoneLineAsync(data).unwrap();
      await toast.promise(promise, {
        pending: "Enregistrement de la ligne...",
        success: "La ligne a bien été enregistrée.",
        error: "La ligne n’a pas pu être enregistrée.",
      });
      ModalRef.current?.showOrHiddenModal();
    } catch (error) {
      const payload = (error as FetchBaseQueryError)?.data as
        { message?: string } | undefined;
      setSubmitError(
        payload?.message ??
          "Impossible d’enregistrer la ligne. Vérifiez les comptes sélectionnés.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onError: SubmitErrorHandler<SoftphoneLineForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title={
        editingId
          ? "Modifier la ligne softphone"
          : "Ajouter une ligne softphone"
      }
      onSubmit={handleSubmit(onSubmit, onError)}
      isLoading={submitting}
    >
      {submitError && (
        <Alert variant="danger" role="alert">
          {submitError}
        </Alert>
      )}
      <Row className="gx-3">
        <Col sm={12}>
          <Form.Group className="mb-3">
            <Form.Label>
              Extension Kavkom <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="ex : 505"
              isInvalid={!!errors.kavkomExtension}
              {...register("kavkomExtension", {
                required: "L'extension Kavkom est requise.",
              })}
            />
            <Form.Control.Feedback type="invalid">
              {errors.kavkomExtension?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={12}>
          <Form.Group className="mb-3">
            <Form.Label>
              UUID utilisateur Kavkom <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              type="text"
              isInvalid={!!errors.kavkomUserUuid}
              {...register("kavkomUserUuid", {
                required: "L'UUID utilisateur Kavkom est requis.",
              })}
            />
            <Form.Control.Feedback type="invalid">
              {errors.kavkomUserUuid?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={12}>
          <Form.Group className="mb-3">
            <Form.Label>Libellé :</Form.Label>
            <Form.Control
              type="text"
              placeholder="ex : Ligne Ilan"
              {...register("label")}
            />
          </Form.Group>
        </Col>
        <Col sm={12}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="softphone-accounts">
              Comptes assignés :
            </Form.Label>
            <Controller
              name="userOptions"
              control={control}
              render={({ field }) => (
                <Select
                  inputId="softphone-accounts"
                  aria-label="Comptes assignés"
                  isMulti
                  value={field.value ?? []}
                  onChange={(options) => field.onChange([...options])}
                  onBlur={field.onBlur}
                  options={userOptions}
                  isDisabled={submitting}
                  isOptionDisabled={(option) =>
                    (field.value?.length ?? 0) >= maxAccounts &&
                    !field.value?.some(
                      (selected) => selected.value === option.value,
                    )
                  }
                  isClearable
                  placeholder="Aucun (ligne non-assignée)"
                />
              )}
            />
            {maxAccounts === 1 && (
              <Form.Text>
                Rechargez la page pour utiliser l’attribution à deux comptes.
              </Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col sm={12}>
          <Form.Group className="mb-3">
            <Form.Label>Statut :</Form.Label>
            <Form.Select {...register("status")}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreSoftphoneLineModal);
